import { LandingNav } from "./LandingNav";
import { LandingFooter } from "./LandingFooter";
import { usePageMeta } from "../../../hooks/usePageMeta";

interface LegalPageProps {
  readonly isDark: boolean;
  readonly onToggleTheme: () => void;
}

export function LegalPage({ isDark, onToggleTheme }: LegalPageProps) {
  usePageMeta(
    "Mentions légales",
    "Informations légales sur Palnia et ses conditions d'utilisation."
  );

  return (
    <div className="min-h-screen bg-surface text-ink antialiased flex flex-col">
      <LandingNav isDark={isDark} onToggleTheme={onToggleTheme} />
      <main className="flex-1 max-w-3xl mx-auto px-6 py-16 w-full">
        <h1 className="text-2xl font-bold mb-8">Mentions légales</h1>

        <Section title="Éditeur du site">
          <p>Palnia est édité par Alane Jaunet, développeur indépendant.</p>
          <p>Contact : contact@newalfox.fr</p>
        </Section>

        <Section title="Hébergement">
          <p>Le site est hébergé sur un serveur personnel géré par l'éditeur, localisé en France.</p>
        </Section>

        <Section title="Propriété intellectuelle">
          <p>
            L'ensemble du contenu du site (textes, interface, code source) est la propriété
            exclusive de l'éditeur, sauf mention contraire. Toute reproduction non autorisée
            est interdite.
          </p>
        </Section>

        <Section title="Responsabilité">
          <p>
            L'éditeur s'efforce de fournir un service fiable mais ne garantit pas
            l'absence d'interruptions ou d'erreurs. L'utilisation du service se fait
            aux risques et périls de l'utilisateur.
          </p>
        </Section>
      </main>
      <LandingFooter />
    </div>
  );
}

export function PrivacyPage({ isDark, onToggleTheme }: LegalPageProps) {
  usePageMeta(
    "Confidentialité",
    "Politique de confidentialité de Palnia : comment vos données sont protégées et utilisées."
  );

  return (
    <div className="min-h-screen bg-surface text-ink antialiased flex flex-col">
      <LandingNav isDark={isDark} onToggleTheme={onToggleTheme} />
      <main className="flex-1 max-w-3xl mx-auto px-6 py-16 w-full">
        <h1 className="text-2xl font-bold mb-8">Politique de confidentialité</h1>

        <Section title="Données collectées">
          <p>Palnia collecte les données suivantes :</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li><strong>Compte</strong> : email, prénom (fournis à l'inscription ou via OAuth)</li>
            <li><strong>Contenu</strong> : tâches, événements, habitudes, notes</li>
            <li><strong>Connexion OAuth</strong> : identifiant et email du fournisseur (Google, GitHub)</li>
            <li><strong>Technique</strong> : adresse IP (logs serveur), cookies de session, données d'usage anonymisées (pages vues, clics, parcours — via PostHog)</li>
          </ul>
        </Section>

        <Section title="Chiffrement de bout en bout">
          <p>
            Tout le contenu utilisateur (titres, notes, tags) est chiffré avec votre mot de passe
            avant d'être envoyé au serveur. L'équipe Palnia ne peut pas lire vos données.
          </p>
          <p>
            Les utilisateurs connectés via OAuth sans mot de passe utilisent un chiffrement
            côté serveur. Ils peuvent définir un mot de passe à tout moment pour activer
            le chiffrement de bout en bout.
          </p>
        </Section>

        <Section title="Finalité du traitement">
          <p>Les données sont utilisées uniquement pour :</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Fournir le service (gestion de tâches, calendrier, habitudes)</li>
            <li>Authentifier l'utilisateur</li>
            <li>Améliorer le service (analytics anonymisés, si activés)</li>
          </ul>
          <p className="mt-2">
            Aucune donnée n'est vendue, louée ou partagée avec des tiers à des fins commerciales.
          </p>
        </Section>

        <Section title="Durée de conservation">
          <p>
            Les données sont conservées tant que le compte est actif. La suppression du compte
            entraîne la suppression définitive de toutes les données associées.
          </p>
        </Section>

        <Section title="Cookies et stockage local">
          <p>Palnia utilise des cookies et le stockage local du navigateur :</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li><strong>Cookie de session</strong> (<code>session_id</code>) : cookie HTTP-only, sécurisé, strictement nécessaire pour maintenir votre connexion. Durée : 30 jours. Non accessible par JavaScript</li>
            <li><strong>Cookies OAuth temporaires</strong> : utilisés pendant le flux de connexion via Google ou GitHub (durée : 10 minutes, supprimés automatiquement après la connexion)</li>
            <li><strong>Stockage local</strong> : préférences d'affichage (thème, sections visibles, paramètres de timers). Ces données restent sur votre appareil et ne sont pas envoyées au serveur</li>
            <li><strong>Analytics</strong> : PostHog utilise un cookie et le localStorage pour attribuer un identifiant anonyme à votre session, afin de mesurer l'utilisation du site (pages vues, clics, parcours). Aucune donnée personnelle n'est collectée par ce biais</li>
          </ul>
          <p className="mt-2">Aucun cookie publicitaire ou de traçage tiers n'est utilisé. Les cookies de session et OAuth sont strictement nécessaires au fonctionnement du service et ne requièrent pas de consentement au titre de la directive ePrivacy.</p>
        </Section>

        <Section title="Vos droits (RGPD)">
          <p>Conformément au RGPD, vous disposez des droits suivants :</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li><strong>Accès</strong> : obtenir une copie de vos données personnelles sur demande</li>
            <li><strong>Rectification</strong> : modifier vos informations dans les Paramètres</li>
            <li><strong>Suppression</strong> : supprimer votre compte depuis les Paramètres</li>
            <li><strong>Portabilité</strong> : recevoir vos données dans un format standard sur demande</li>
          </ul>
          <p className="mt-2">
            Pour exercer vos droits ou poser une question : contact@newalfox.fr
          </p>
        </Section>

        <Section title="Sous-traitants">
          <p>Les services tiers suivants peuvent traiter des données techniques :</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li><strong>Sentry</strong> (Functional Software, Inc.) : monitoring d'erreurs — traces techniques uniquement (pas de contenu utilisateur)</li>
            <li><strong>PostHog</strong> (PostHog, Inc. — hébergement UE) : analytics d'usage — pages vues, clics, parcours utilisateur. Autocapture désactivé, seuls les événements définis explicitement sont collectés. L'enregistrement de session (session replay) est activé pour améliorer l'expérience utilisateur : il capture la structure des pages et les interactions (clics, scroll), mais tout le contenu textuel de l'application est automatiquement masqué. Aucun contenu chiffré n'est visible dans les replays. Les données analytics transitent via un proxy géré par Cloudflare (reverse proxy)</li>
            <li><strong>Cloudflare</strong> (Cloudflare, Inc.) : proxy d'infrastructure utilisé par PostHog pour acheminer les données analytics — aucun accès au contenu utilisateur</li>
            <li><strong>Google, GitHub</strong> : fournisseurs OAuth pour l'authentification uniquement</li>
          </ul>
          <p className="mt-2">
            Aucun sous-traitant n'a accès au contenu chiffré de vos données.
          </p>
        </Section>

        <p className="text-xs text-ink-faint mt-12">
          Dernière mise à jour : 27 mars 2026
        </p>
      </main>
      <LandingFooter />
    </div>
  );
}

function Section({ title, children }: { readonly title: string; readonly children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-semibold mb-3">{title}</h2>
      <div className="text-sm text-ink-muted leading-relaxed space-y-2">{children}</div>
    </section>
  );
}
